import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-tabs.css';

export type ExtTabsVariant = 'underline' | 'pills';

export interface ExtTab {
  id: string;
  label: string;
  icon?: string;
  badge?: string | number;
  disabled?: boolean;
}

/**
 * <ext-tabs .tabs=${[...]} active="tab1" variant="underline">
 *   <div data-tab="tab1">Contenido 1</div>
 *   <div data-tab="tab2">Contenido 2</div>
 * </ext-tabs>
 *
 * Solo renderiza la barra de tabs. El user filtra el contenido segun
 * el tab activo (via @ext:change handler).
 *
 * Light DOM: NO usamos render() con template porque romperia los childs
 * que el user pasa como content. En su lugar:
 *   - En connectedCallback capturamos los childs del user y los movemos
 *     a un wrapper .ext-tabs__panel (se hace una sola vez).
 *   - Prependemos una barra .ext-tabs__list que renderizamos imperativamente.
 *   - La barra se re-renderiza en updated() cuando cambian tabs/active.
 *
 * Eventos:
 *  - ext:change - { id, tab }
 *
 * Keyboard:
 *  - ArrowLeft / ArrowRight: navega entre tabs (skip disabled).
 *  - Home / End: primero / ultimo.
 *  - Enter / Space: activa tab foqueado.
 */
@customElement('ext-tabs')
export class ExtTabs extends ExtBaseElement {
  @property({ attribute: false }) tabs: ExtTab[] = [];
  @property({ type: String, reflect: true }) active = '';
  @property({ type: String, reflect: true }) variant: ExtTabsVariant = 'underline';
  @property({ type: Boolean, attribute: 'full-width', reflect: true }) fullWidth = false;

  private _structured = false;
  private _listEl: HTMLDivElement | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    return null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._structure();
    this._renderList();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('tabs') || changed.has('active') || changed.has('variant') || changed.has('fullWidth')) {
      this._renderList();
    }
  }

  private _structure(): void {
    if (this._structured) return;
    const userNodes = Array.from(this.childNodes);
    this.innerHTML = '';

    const list = document.createElement('div');
    list.className = 'ext-tabs__list';
    list.setAttribute('role', 'tablist');
    this._listEl = list;

    const panel = document.createElement('div');
    panel.className = 'ext-tabs__panel';
    panel.setAttribute('role', 'tabpanel');
    userNodes.forEach((n) => panel.appendChild(n));

    this.appendChild(list);
    this.appendChild(panel);
    this._structured = true;
  }

  private _activate(tab: ExtTab): void {
    if (tab.disabled) return;
    if (tab.id === this.active) return;
    this._emit('change', { id: tab.id, tab });
  }

  private _onKeyDown(ev: KeyboardEvent, index: number): void {
    const key = ev.key;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' '].includes(key)) return;
    ev.preventDefault();

    if (key === 'Enter' || key === ' ') {
      const t = this.tabs[index];
      if (t) this._activate(t);
      return;
    }

    const count = this.tabs.length;
    if (count === 0) return;
    let nextIndex = index;
    const move = (delta: number): number => {
      let i = index;
      for (let n = 0; n < count; n++) {
        i = (i + delta + count) % count;
        if (!this.tabs[i].disabled) return i;
      }
      return index;
    };

    if (key === 'ArrowLeft') nextIndex = move(-1);
    else if (key === 'ArrowRight') nextIndex = move(1);
    else if (key === 'Home') {
      const f = this.tabs.findIndex((t) => !t.disabled);
      nextIndex = f === -1 ? index : f;
    } else if (key === 'End') {
      for (let i = count - 1; i >= 0; i--) {
        if (!this.tabs[i].disabled) {
          nextIndex = i;
          break;
        }
      }
    }

    const next = this.tabs[nextIndex];
    if (next && !next.disabled) {
      this._activate(next);
      requestAnimationFrame(() => {
        const btn = this._listEl?.querySelector<HTMLElement>(
          `[data-tab-id="${CSS.escape(next.id)}"]`
        );
        btn?.focus();
      });
    }
  }

  private _renderList(): void {
    if (!this._listEl) return;
    const list = this._listEl;
    list.innerHTML = '';

    this.tabs.forEach((tab, idx) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      const classes = ['ext-tabs__tab'];
      const isActive = tab.id === this.active;
      if (isActive) classes.push('is-active');
      if (tab.disabled) classes.push('is-disabled');
      btn.className = classes.join(' ');
      btn.setAttribute('role', 'tab');
      btn.dataset.tabId = tab.id;
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.setAttribute('aria-disabled', tab.disabled ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;
      if (tab.disabled) btn.disabled = true;

      if (tab.icon) {
        const ic = document.createElement('ext-icon');
        ic.className = 'ext-tabs__icon';
        ic.setAttribute('name', tab.icon);
        ic.setAttribute('size', 'sm');
        ic.setAttribute('aria-hidden', 'true');
        btn.appendChild(ic);
      }

      const lbl = document.createElement('span');
      lbl.className = 'ext-tabs__label';
      lbl.textContent = tab.label;
      btn.appendChild(lbl);

      if (tab.badge !== undefined && tab.badge !== null && tab.badge !== '') {
        const bd = document.createElement('span');
        bd.className = 'ext-tabs__badge';
        bd.textContent = String(tab.badge);
        btn.appendChild(bd);
      }

      btn.addEventListener('click', () => this._activate(tab));
      btn.addEventListener('keydown', (ev) => this._onKeyDown(ev, idx));

      list.appendChild(btn);
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-tabs': ExtTabs;
  }
}
